import { createContext, useContext, useMemo } from "react";

import { useLogger } from "../../logger";
import { PageStateContext } from "../Page";
import type { OdigeoRouterNavigate } from "./router";
import { createRouter } from "./router";
import { useRouteMatcher } from "./RoutesProvider";

export type QueryParameters = Record<string, string | string[]>;

export interface Router<Query extends QueryParameters> {
  pathname: string;
  query: Query;
  push: (url: string) => void;
  replace: (url: string) => void;
  back: () => void;
}

export type RouterHook = <
  Query extends QueryParameters = QueryParameters,
>() => Router<Query>;

export const OdigeoRouterNavigateContext = createContext<OdigeoRouterNavigate>(
  () => {},
);

const useOdigeoRouterNavigate = () => useContext(OdigeoRouterNavigateContext);

export const useLegacyRouter: RouterHook = () => {
  const routeMatcher = useRouteMatcher();
  const logger = useLogger();
  const page = useContext(PageStateContext);
  const { params } = page;
  const odigeoNavigate = useOdigeoRouterNavigate();
  return useMemo(
    () =>
      createRouter({
        routeMatcher,
        odigeoNavigate,
        logger,
        params,
      }),
    [routeMatcher, odigeoNavigate, logger, params],
  );
};

const useServerFakeRouter = <
  Query extends QueryParameters,
>(): Router<Query> => ({
  pathname: "",
  query: {} as Query,
  push: () => {},
  replace: () => {},
  back: () => {},
});

export const UseRouterContext = createContext<RouterHook>(
  typeof window === "undefined" ? useServerFakeRouter : useLegacyRouter,
);
